import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Typography
} from '@material-ui/core';
import styled from 'styled-components';

import { OrgChartNode, OrgChartResponse } from 'domain-types';
import { AuthContext, baseUrl } from 'framework';
import { Line, TitleContainer } from 'components';
import { StaffTabs } from './staff-tabs';

const TreeRoot = styled(Box)`
  margin-top: ${p => p.theme.spacing(2)}px;
  overflow-x: auto;
`;

const StyledSvg = styled.svg`
  min-width: 100%;
`;

const NODE_WIDTH = 240;
const NODE_HEIGHT = 80;
const HORIZONTAL_GAP = 36;
const VERTICAL_GAP = 44;
const SVG_PADDING = 24;

const AVATAR_CX = 30;
const AVATAR_CY = 40;
const AVATAR_R = 22;

// x-center of the text area (right of avatar: 60–240 → center = 150)
const TEXT_CENTER_X = 150;

const INITIALS_FONT_SIZE = 13;
const INITIALS_Y_OFFSET = 5; // baseline shift so text is visually centred in circle
const NAME_FONT_SIZE = 15;
const TITLE_FONT_SIZE = 13;

const AVATAR_COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#c62828',
  '#00796b',
  '#0288d1',
  '#ad1457'
];

const getAvatarColor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

type PositionedNode = {
  id: string;
  name: string;
  title?: string;
  avatarUrl?: string;
  x: number;
  y: number;
  parentId?: string;
};

const normalizeTitle = (title?: string) => {
  const trimmedTitle = title?.trim();
  return trimmedTitle || undefined;
};

const layoutOrgChart = (nodes: OrgChartNode[]) => {
  let currentColumn = 0;
  const positionedNodes: PositionedNode[] = [];

  const placeNode = (node: OrgChartNode, depth: number, parentId?: string): number => {
    if (!node.reports.length) {
      const leafX = currentColumn;
      currentColumn += 1;
      positionedNodes.push({
        id: node.id,
        name: node.name,
        title: normalizeTitle(node.title),
        avatarUrl: node.avatarUrl,
        x: leafX * (NODE_WIDTH + HORIZONTAL_GAP),
        y: depth * (NODE_HEIGHT + VERTICAL_GAP),
        parentId
      });
      return leafX;
    }

    const firstChildX = placeNode(node.reports[0], depth + 1, node.id);
    let lastChildX = firstChildX;
    for (let index = 1; index < node.reports.length; index += 1) {
      lastChildX = placeNode(node.reports[index], depth + 1, node.id);
    }

    const centerX = (firstChildX + lastChildX) / 2;
    positionedNodes.push({
      id: node.id,
      name: node.name,
      title: normalizeTitle(node.title),
      avatarUrl: node.avatarUrl,
      x: centerX * (NODE_WIDTH + HORIZONTAL_GAP),
      y: depth * (NODE_HEIGHT + VERTICAL_GAP),
      parentId
    });
    return centerX;
  };

  nodes.forEach(root => {
    placeNode(root, 0);
    currentColumn += 1;
  });

  return positionedNodes;
};

const SvgNode = styled.g`
  rect {
    fill: #ffffff;
    stroke: #1976d2;
    stroke-width: 1.5;
    rx: 8;
  }
`;

const NodeAvatar = ({
  nodeId,
  name,
  avatarUrl
}: {
  nodeId: string;
  name: string;
  avatarUrl?: string;
}) => {
  const [imgError, setImgError] = useState(false);
  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);
  const showImage = !!avatarUrl && !imgError;
  const color = getAvatarColor(nodeId);
  const initials = getInitials(name);
  const clipId = `avatar-clip-${nodeId}`;

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <circle cx={AVATAR_CX} cy={AVATAR_CY} r={AVATAR_R} />
        </clipPath>
      </defs>
      <circle cx={AVATAR_CX} cy={AVATAR_CY} r={AVATAR_R} fill={color} />
      {showImage ? (
        <image
          href={avatarUrl}
          x={AVATAR_CX - AVATAR_R}
          y={AVATAR_CY - AVATAR_R}
          width={AVATAR_R * 2}
          height={AVATAR_R * 2}
          clipPath={`url(#${clipId})`}
          preserveAspectRatio="xMidYMid slice"
          // @ts-expect-error — SVG <image> loading attribute is valid in modern browsers but not in React's SVG typings
          loading="lazy"
          role="img"
          aria-label={name}
          onError={() => setImgError(true)}
          data-testid={`avatar-img-${nodeId}`}
        />
      ) : (
        <text
          x={AVATAR_CX}
          y={AVATAR_CY + INITIALS_Y_OFFSET}
          textAnchor="middle"
          fill="white"
          fontSize={INITIALS_FONT_SIZE}
          fontWeight="bold"
          aria-hidden="true"
          data-testid={`avatar-initials-${nodeId}`}
        >
          {initials}
        </text>
      )}
    </>
  );
};

const OrgChartNodeCard = ({ node }: { node: PositionedNode }) => (
  <SvgNode
    transform={`translate(${node.x + SVG_PADDING},${node.y + SVG_PADDING})`}
  >
    <rect width={NODE_WIDTH} height={NODE_HEIGHT} />
    <NodeAvatar nodeId={node.id} name={node.name} avatarUrl={node.avatarUrl} />
    <text
      x={TEXT_CENTER_X}
      y={node.title ? 34 : 44}
      textAnchor="middle"
      fill="#263238"
      fontSize={NAME_FONT_SIZE}
      fontWeight="bold"
    >
      {node.name}
    </text>
    {!!node.title && (
      <text
        x={TEXT_CENTER_X}
        y={56}
        textAnchor="middle"
        fill="#546e7a"
        fontSize={TITLE_FONT_SIZE}
      >
        ({node.title})
      </text>
    )}
  </SvgNode>
);

const renderTree = (nodes: OrgChartNode[]) => {
  const positionedNodes = layoutOrgChart(nodes);
  const nodeMap = new Map(positionedNodes.map(node => [node.id, node]));
  const maxX = positionedNodes.length
    ? Math.max(...positionedNodes.map(node => node.x))
    : 0;
  const maxY = positionedNodes.length
    ? Math.max(...positionedNodes.map(node => node.y))
    : 0;
  const svgWidth = Math.max(700, maxX + NODE_WIDTH + SVG_PADDING * 2);
  const svgHeight = maxY + NODE_HEIGHT + SVG_PADDING * 2;

  return (
    <StyledSvg width={svgWidth} height={svgHeight} role="img" aria-label="Org chart">
      {positionedNodes.map(node => {
        const parent = node.parentId ? nodeMap.get(node.parentId) : undefined;
        if (!parent) {
          return null;
        }
        return (
          <line
            key={`${parent.id}-${node.id}`}
            x1={parent.x + NODE_WIDTH / 2 + SVG_PADDING}
            y1={parent.y + NODE_HEIGHT + SVG_PADDING}
            x2={node.x + NODE_WIDTH / 2 + SVG_PADDING}
            y2={node.y + SVG_PADDING}
            stroke="#90a4ae"
            strokeWidth={1.5}
          />
        );
      })}
      {positionedNodes.map(node => (
        <OrgChartNodeCard key={node.id} node={node} />
      ))}
    </StyledSvg>
  );
};

const getErrorMessage = (t: (key: string) => string, err: unknown) => {
  if (Array.isArray(err)) {
    const error = err[0] as { response?: { error?: string } };
    if (error?.response?.error) {
      return error.response.error;
    }
  }

  return t('orgChartLoadFailed');
};

export function OrgChart() {
  const { organization } = useParams<{ organization: string }>();
  const { t } = useTranslation();
  const { getToken } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orgChart, setOrgChart] = useState<OrgChartResponse | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const pendingPdfUrlsRef = useRef<string[]>([]);
  const pendingRevokeTimersRef = useRef<number[]>([]);

  useEffect(
    () => () => {
      pendingRevokeTimersRef.current.forEach(timerId =>
        window.clearTimeout(timerId)
      );
      pendingPdfUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    },
    []
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      setOrgChart(null);
      try {
        const response = await fetch(`${baseUrl}/org/${organization}/org-chart`, {
          headers: {
            Authorization: `Bearer ${getToken().getJwtToken()}`
          }
        });

        if (!response.ok) {
          const payload = (await response.json()) as { error?: string };
          throw new Error(payload.error || t('orgChartLoadFailed'));
        }

        const payload = (await response.json()) as OrgChartResponse;
        setOrgChart(payload);
      } catch (err) {
        setError(err instanceof Error ? err.message : getErrorMessage(t, err));
      } finally {
        setLoading(false);
      }
    })();
  }, [organization, getToken, t]);

  const onDownloadPdf = useCallback(async () => {
    setIsDownloadingPdf(true);
    setError('');
    try {
      const response = await fetch(`${baseUrl}/org/${organization}/org-chart/pdf`, {
        headers: {
          Authorization: `Bearer ${getToken().getJwtToken()}`
        }
      });

      if (!response.ok) {
        let serverError = t('orgChartPdfFailed');
        try {
          const payload = (await response.json()) as { error?: string };
          if (payload.error) {
            serverError = payload.error;
          }
        } catch {
          // No-op fallback to default translated message.
        }
        throw new Error(serverError);
      }

      const blob = await response.blob();
      const nextPdfUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = nextPdfUrl;
      link.download = 'org-chart.pdf';
      link.click();
      link.remove();
      pendingPdfUrlsRef.current.push(nextPdfUrl);
      const revokeTimerId = window.setTimeout(() => {
        URL.revokeObjectURL(nextPdfUrl);
        pendingPdfUrlsRef.current = pendingPdfUrlsRef.current.filter(
          url => url !== nextPdfUrl
        );
        pendingRevokeTimersRef.current = pendingRevokeTimersRef.current.filter(
          timerId => timerId !== revokeTimerId
        );
      }, 30000);
      pendingRevokeTimersRef.current.push(revokeTimerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('orgChartPdfFailed'));
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [getToken, organization, t]);

  const subtitle = orgChart?.generatedAt
    ? `${t('generatedOn')}: ${new Date(orgChart.generatedAt).toLocaleString()}`
    : '';

  return (
    <StaffTabs organization={organization} selected="orgChart">
      <TitleContainer>
        <Box>
          {!!subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Button
          color="primary"
          variant="contained"
          disabled={loading || isDownloadingPdf || !!error}
          onClick={onDownloadPdf}
        >
          {isDownloadingPdf ? t('downloadingPdf') : t('downloadPdf')}
        </Button>
      </TitleContainer>
      <Line />
      {loading && (
        <Box mt={2} display="flex" alignItems="center">
          <CircularProgress size={20} />
          <Box ml={1}>
            <Typography variant="body2">{t('loading')}</Typography>
          </Box>
        </Box>
      )}
      {!loading && !!error && (
        <Box mt={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      {!loading && !error && !orgChart?.hasRelationships && (
        <Box mt={2}>
          <Typography>{t('orgChartEmptyState')}</Typography>
        </Box>
      )}
      {!loading && !error && orgChart?.hasRelationships && (
        <TreeRoot>{renderTree(orgChart.nodes)}</TreeRoot>
      )}
    </StaffTabs>
  );
}
